import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Globe,
  DollarSign,
  HelpCircle,
  KeyRound,
  Lock,
  LogOut,
  ChevronRight,
  Moon,
  ScanFace,
  Sun
} from "lucide-react";
import { useApp } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "react-i18next";
import { openSupportChat } from "@/lib/support";
import PinSetupSheet from "@/components/PinSetupSheet";
import { generateBiometricSecret, getBiometricManager } from "@/lib/appLock";

const languages = [
  { code: 'en', name: 'English' },
  { code: 'ru', name: 'Русский' },
  { code: 'es', name: 'Español' },
  { code: 'zh', name: '中文' },
];

const currencies = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    user, api, haptic, telegram, preferredCurrency, setPreferredCurrency, syncTelegramTheme,
    pinEnabled, setPinConfigured, refreshSecurityState,
  } = useApp();
  const [avatarError, setAvatarError] = useState(false);
  const profilePhotoUrl = String(user?.photo_url || user?.settings?.photo_url || "").trim();

  const [language, setLanguage] = useState(i18n.language || 'en');
  const [darkMode, setDarkMode] = useState(
    document.documentElement.classList.contains('dark')
  );

  // Код-пароль и биометрия.
  const [pinSheetMode, setPinSheetMode] = useState(null); // null | create | change | disable
  const [bioAvailable, setBioAvailable] = useState(false);
  const [bioType, setBioType] = useState("unknown");
  const [bioEnabled, setBioEnabled] = useState(Boolean(user?.settings?.biometric_enabled));
  const [bioBusy, setBioBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { available, type } = await getBiometricManager();
      if (cancelled) return;
      setBioAvailable(available);
      setBioType(type);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePinToggle = () => {
    haptic('light');
    setPinSheetMode(pinEnabled ? 'disable' : 'create');
  };

  const handlePinSheetSuccess = async (mode) => {
    setPinSheetMode(null);
    if (mode === 'disable') {
      setPinConfigured(false);
      setBioEnabled(false);
      toast.success(t('security.pin_disabled_toast'));
    } else {
      setPinConfigured(true);
      toast.success(mode === 'change' ? t('security.pin_changed_toast') : t('security.pin_enabled_toast'));
    }
  };

  const handleBiometricToggle = async () => {
    if (bioBusy) return;
    haptic('light');
    setBioBusy(true);
    try {
      const { bm, available } = await getBiometricManager();
      if (!bm || !available) {
        toast.error(t('security.bio_unavailable'));
        return;
      }
      if (bioEnabled) {
        // Выключение: сервер + чистим секрет на устройстве.
        await api.post('/security/biometric/disable');
        try {
          bm.updateBiometricToken("", () => {});
        } catch (_) { /* noop */ }
        setBioEnabled(false);
        toast.success(t('security.bio_disabled_toast'));
        return;
      }

      // Включение: доступ -> секрет на устройство -> регистрация на сервере.
      const granted = await new Promise((resolve) => {
        try {
          if (bm.isAccessGranted) return resolve(true);
          bm.requestAccess({ reason: t('security.bio_reason') }, (ok) => resolve(Boolean(ok)));
        } catch (_) {
          resolve(false);
        }
      });
      if (!granted) {
        toast.error(t('security.bio_access_denied'));
        try {
          bm.openSettings?.();
        } catch (_) { /* noop */ }
        return;
      }

      const secret = generateBiometricSecret();
      const applied = await new Promise((resolve) => {
        try {
          bm.updateBiometricToken(secret, (ok) => resolve(Boolean(ok)));
        } catch (_) {
          resolve(false);
        }
      });
      if (!applied) {
        toast.error(t('security.bio_save_failed'));
        return;
      }

      try {
        // Требует свежий unlock-тикет: при его отсутствии 428 сам покажет экран кода.
        await api.post('/security/biometric/enable', { secret });
        setBioEnabled(true);
        toast.success(t('security.bio_enabled_toast'));
      } catch (err) {
        // Сервер отказал — откатываем секрет с устройства, иначе вечный biometric_stale.
        try {
          bm.updateBiometricToken("", () => {});
        } catch (_) { /* noop */ }
        toast.error(t('security.generic_error'));
      }
    } finally {
      setBioBusy(false);
      refreshSecurityState?.();
    }
  };

  const handleLanguageChange = async (value) => {
    haptic('light');
    setLanguage(value);
    i18n.changeLanguage(value);
    try {
      await api.put('/user/settings', { language_code: value });
      toast.success(t('common.success_language'));
    } catch (err) {
      console.error('Failed to update language:', err);
    }
  };

  const handleCurrencyChange = async (value) => {
    haptic('light');
    await setPreferredCurrency(value);
    toast.success(t('common.success_currency'));
  };

  const handleDarkModeToggle = () => {
    haptic('light');
    const newMode = !darkMode;
    setDarkMode(newMode);

    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Sync theme with Telegram UI
    if (syncTelegramTheme) {
      syncTelegramTheme(newMode);
    }

    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  const handleSupport = () => {
    haptic('light');
    openSupportChat(telegram);
  };

  const handleLogout = async () => {
    haptic('medium');
    try {
      await api.post('/auth/logout');
    } catch (_) {
      // Logout should still clear local session even if backend call fails.
    }
    try {
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');
    } catch (_) {
      // ignore storage errors
    }
    telegram?.close?.();
    navigate('/');
    window.location.reload();
  };

  const displayName = user?.username || user?.first_name || "user";
  const secondaryLine = user?.telegram_username
    ? `@${user.telegram_username}`
    : (user?.email || "user");

  useEffect(() => {
    setAvatarError(false);
  }, [profilePhotoUrl]);

  return (
    <div className="px-4 pt-[calc(0.5rem+var(--lp-top))] pb-0 space-y-2 animate-fade-in" data-testid="profile-page">
      {/* User Info */}
      <button
        type="button"
        className="lp-press lp-glass w-full flex items-center gap-4 p-3 mt-1 text-left"
        onClick={() => navigate("/profile/account")}
      >
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
          {profilePhotoUrl && !avatarError ? (
            <img
              src={profilePhotoUrl}
              alt={displayName}
              className="w-full h-full object-cover"
              onError={() => setAvatarError(true)}
            />
          ) : (
            <span className="text-2xl font-bold text-link">
              {(displayName?.[0] || 'U').toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold leading-tight">{displayName}</h2>
          <p className="text-muted-foreground text-sm break-all">
            {secondaryLine}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </button>

      {/* Settings */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground px-1">{t('profile.settings')}</h3>

        <div className="lp-glass divide-y divide-[var(--lp-hair)]">
          {/* Dark Mode */}
          <div className="p-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {darkMode ? (
                <Moon className="w-5 h-5 text-muted-foreground" />
              ) : (
                <Sun className="w-5 h-5 text-muted-foreground" />
              )}
              <span className="font-medium">{t('profile.dark_mode')}</span>
            </div>
            <Switch
              checked={darkMode}
              onCheckedChange={handleDarkModeToggle}
              data-testid="dark-mode-toggle"
            />
          </div>

          {/* Language */}
          <div className="p-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium">{t('profile.language')}</span>
            </div>
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger className="w-32" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fiat Currency */}
          <div className="p-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium">{t('profile.display_currency')}</span>
            </div>
            <Select value={preferredCurrency} onValueChange={handleCurrencyChange}>
              <SelectTrigger className="w-32" data-testid="currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    {curr.symbol} {curr.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* PIN code */}
          <div className="p-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <div>
                <span className="font-medium">{t('security.pin_row_title')}</span>
                <p className="text-xs text-muted-foreground">{t('security.pin_row_subtitle')}</p>
              </div>
            </div>
            <Switch
              checked={Boolean(pinEnabled)}
              onCheckedChange={handlePinToggle}
              data-testid="pin-toggle"
            />
          </div>

          {/* Face ID / fingerprint */}
          <div className="p-3.5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ScanFace className="w-5 h-5 text-muted-foreground" />
              <div>
                <span className="font-medium">
                  {bioType === 'finger' ? t('security.bio_row_title_finger') : t('security.bio_row_title')}
                </span>
                <p className="text-xs text-muted-foreground">
                  {!bioAvailable
                    ? t('security.bio_row_unavailable')
                    : !pinEnabled
                      ? t('security.bio_row_needs_pin')
                      : t('security.bio_row_subtitle')}
                </p>
              </div>
            </div>
            <Switch
              checked={Boolean(bioEnabled)}
              onCheckedChange={handleBiometricToggle}
              disabled={!bioAvailable || !pinEnabled || bioBusy}
              data-testid="biometric-toggle"
            />
          </div>
        </div>
      </div>

      {/* Security */}
      {pinEnabled && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground px-1">{t('security.section_title')}</h3>

          <div className="lp-glass">
            <button
              onClick={() => {
                haptic('light');
                setPinSheetMode('change');
              }}
              className="lp-press w-full p-3.5 flex items-center justify-between hover:bg-[var(--lp-tile)] rounded-2xl transition-colors"
              data-testid="change-pin-btn"
            >
              <div className="flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-muted-foreground" />
                <div className="text-left">
                  <span className="font-medium">{t('security.change_pin_title')}</span>
                  <p className="text-xs text-muted-foreground">{t('security.change_pin_subtitle_row')}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>
        </div>
      )}

      {/* Support */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground px-1">{t('profile.support')}</h3>

        <div className="lp-glass">
          <button
            onClick={handleSupport}
            className="lp-press w-full p-3.5 flex items-center justify-between hover:bg-[var(--lp-tile)] rounded-2xl transition-colors"
            data-testid="support-btn"
          >
            <div className="flex items-center gap-3">
              <HelpCircle className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium">{t('profile.help_support')}</span>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Logout */}
      <Button
        onClick={handleLogout}
        variant="outline"
        className="w-full h-12 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 text-base font-semibold"
        data-testid="logout-btn"
      >
        <LogOut className="w-5 h-5 mr-3" />
        {t('profile.logout')}
      </Button>

      {pinSheetMode && (
        <PinSetupSheet
          api={api}
          user={user}
          mode={pinSheetMode}
          onClose={() => setPinSheetMode(null)}
          onSuccess={handlePinSheetSuccess}
          onLinkEmail={() => {
            setPinConfigured(true);
            setPinSheetMode(null);
            navigate('/profile/account');
          }}
        />
      )}
    </div>
  );
};

export default ProfilePage;
